import type React from 'react';
import { Box, Stack, Text, TextLink, Alert } from 'braid-design-system';
import { observer } from 'mobx-react';
import { APPLIED } from '../../../../constants/sourceId';
import * as styles from '../PreviousApplications.css';
import TimelineItem from './TimelineItem';
import Timeline from './Timeline';
import { useStores } from 'src/utils/hooks/useStores';
import DateFormat from 'src/components/common/DateFormat';
import type { IProspect } from 'src/stores/JobApplicationStore/CandidateStore';
import AttachmentList from 'src/components/list/JobApplicationCard/ExtendedInfoList/AttachmentList';
import translations from './.vocab/';
import { useTranslations } from '@vocab/react';
import type { EmployerLocale } from 'src/siteConfig';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';

interface Props {
  previousApplication?: IProspect;
  locale: EmployerLocale;
  isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
}

const Details: React.FC<Props> = ({
  previousApplication,
  locale,
  isVerifiedId,
}) => {
  const { t } = useTranslations(translations);
  const { isMobile } = useStores();

  if (!previousApplication) {
    return null;
  }

  const { source, positionSummary, appliedDate, attachments, candidateId } =
    previousApplication;
  const { hasAccess } = positionSummary;
  const isSeekApply = source === APPLIED;

  return (
    <Box marginTop={['xxsmall', 'large']} className={styles.appContainer}>
      <Stack space="medium">
        {!isMobile && <Text size="large">{positionSummary.title}</Text>}
        {isMobile && (
          <Text tone="secondary">
            <DateFormat
              date={previousApplication.appliedDate}
              locale={locale}
            />
          </Text>
        )}
        <Text>
          <TextLink href={`?id=${positionSummary.id}`}>
            {t('View job details')}
          </TextLink>
        </Text>

        {hasAccess ? (
          <>
            <Box marginTop="small">
              <Text weight="strong">{t('Application timeline')}</Text>
            </Box>
            <Timeline
              previousApplication={previousApplication}
              locale={locale}
            />
            <TimelineItem date={appliedDate} locale={locale}>
              <Text>
                {isSeekApply ? t('Applied via Seek') : t('Manually added')}
              </Text>
            </TimelineItem>
            {attachments.length > 0 && (
              <>
                <Box marginY="small">
                  <Text weight="strong">{t('Application documents')}</Text>
                </Box>
                <Box paddingY="medium" className={styles.attachments}>
                  <AttachmentList
                    attachments={attachments}
                    candidateId={candidateId}
                    isVerifiedId={isVerifiedId}
                  />
                </Box>
              </>
            )}
          </>
        ) : (
          <Alert tone="critical">
            <Text>
              {t(
                'Only some people can see this job. If you think you should be one of them, ask your system administrator to give you access.',
              )}
            </Text>
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

export default observer(Details);
